import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { http, formatIDR } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { TrendUp, Trophy, Briefcase, Calendar, UsersThree, ListChecks, Target, ChartLineUp, Sparkle } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const stageOrder = [
  "lead",
  "quotation",
  "discuss_mgmt",
  "negotiation",
  "invoice",
  "payment",
  "pending",
  "lose",
];
const stageColors = {
  lead: "#5A6B76",
  quotation: "#0284C7",
  discuss_mgmt: "#7C3AED",
  negotiation: "#4F46E5",
  invoice: "#D97706",
  payment: "#16A34A",
  pending: "#F59E0B",
  lose: "#DC2626",
};

function KPICard({ label, value, icon: Icon, accent, testid, sub }) {
  return (
    <div
      className="bg-white border border-[#E2E8F0] border-t-4 p-3 sm:p-4 lg:p-5 flex flex-col justify-between gap-1 sm:gap-2 hover:-translate-y-0.5 transition-transform duration-200 shadow-2xs rounded-b-lg"
      style={{ borderTopColor: accent }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.1em] font-bold text-[#64748B] truncate">{label}</div>
        <Icon size={16} className="text-[#64748B] shrink-0" />
      </div>
      <div className="font-mono-data text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-[#173042] leading-tight tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">{value}</div>
      {sub ? (
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.1em] text-[#64748B] truncate">{sub}</div>
      ) : (
        <div className="h-2" />
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([http.get("/analytics/summary"), http.get("/activities")]);
        setSummary(s.data);
        setActivities(a.data.slice(0, 8));
      } catch (e) { /* noop */ }
    })();
  }, []);

  const chartData = summary
    ? stageOrder.map((s) => ({
        stage: t(`stage.${s}`), key: s,
        count: summary.stage_counts?.[s] || 0,
        value: summary.stage_values?.[s] || 0,
        weighted: summary.stage_weighted?.[s] || 0,
      }))
    : [];

  const forecast = summary?.forecast;
  const forecastTotal = forecast ? forecast.weighted + forecast.won_value : 0;

  return (
    <AppLayout>
      {/* Responsive Dashboard Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Overview</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]" data-testid="dashboard-title">
            Halo, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">
            Ini ringkasan performa penjualan {user?.role === "sales" ? "Anda" : "tim"} hari ini.
          </p>
        </div>
        <div className="pt-2 sm:pt-0 border-t sm:border-t-0 border-[#E2E8F0] sm:text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-bold">Hari ini</div>
          <div className="font-mono-data text-xs sm:text-sm lg:text-base font-semibold text-[#173042]">
            {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </div>
      </div>

      {/* Primary 4-KPI Grid (2-col on mobile, 4-col on lg) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 stagger">
        <KPICard testid="kpi-pipeline" label={t("kpi.pipeline")} icon={TrendUp} accent="#1C4E63" value={summary ? formatIDR(summary.pipeline_value) : "-"} />
        <KPICard testid="kpi-won" label={t("kpi.won")} icon={Trophy} accent="#2F7D32" value={summary ? formatIDR(summary.won_value) : "-"} />
        <KPICard testid="kpi-open-deals" label={t("kpi.openDeals")} icon={Briefcase} accent="#0F2A3A" value={summary?.open_deals ?? "-"} />
        <KPICard testid="kpi-meetings" label={t("kpi.meetings")} icon={Calendar} accent="#D9A321" value={summary?.meetings_this_week ?? "-"} />
      </div>

      {/* FORECAST STRIP - clickable to detail */}
      <Link to="/forecast" className="block mt-4 sm:mt-5 group" data-testid="forecast-link">
        <div className="bg-[#173B4D] text-white p-4 sm:p-6 lg:p-8 rounded-xl border border-[#24566D]/50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative overflow-hidden group-hover:bg-[#1C4E63] transition-colors shadow-xs" data-testid="forecast-panel">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full brand-dot-accent opacity-20" />
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#D9A321] font-bold">
              <Sparkle size={14} weight="fill" /> {user?.role === "sales" ? "Forecast Saya" : "Forecast Tim"}
            </div>
            <div className="font-display text-lg sm:text-2xl mt-1.5 leading-snug">Proyeksi Bulan Ini</div>
            <div className="text-xs text-white/70 mt-1.5 leading-relaxed">
              Klik untuk buka rincian forecast per sales & deal.
            </div>
          </div>
          <ForecastTile testid="fc-weighted" label="Weighted Pipeline" value={forecast ? formatIDR(forecast.weighted) : "-"} icon={ChartLineUp} muted />
          <ForecastTile testid="fc-commit" label="Commit (Proposal + Nego)" value={forecast ? formatIDR(forecast.commit) : "-"} icon={Target} />
          <ForecastTile testid="fc-total" label="Proyeksi Total" value={formatIDR(forecastTotal)} sub={`= Weighted + Won bulan ini`} icon={Trophy} highlight />
        </div>
      </Link>

      {/* Chart + Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 sm:mt-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 shadow-2xs">
          <div className="flex items-baseline justify-between mb-4 sm:mb-6">
            <div>
              <h3 className="font-display text-lg sm:text-2xl tracking-tight text-[#173042]">Distribusi Deal per Tahap</h3>
              <div className="text-[10px] sm:text-xs text-[#64748B] mt-0.5 uppercase tracking-[0.12em] font-semibold">Jumlah dan nilai deal per tahap</div>
            </div>
          </div>
          <div className="h-56 sm:h-72 w-full" data-testid="pipeline-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="stage" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={{ stroke: "#E2E8F0" }} interval={0} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={{ stroke: "#E2E8F0" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 11, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
                  formatter={(val, _n, entry) => [`${val} deal · ${formatIDR(entry.payload.value)} · weighted ${formatIDR(entry.payload.weighted)}`, entry.payload.stage]}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {chartData.map((d) => <Cell key={d.key} fill={stageColors[d.key]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Forecast breakdown per stage */}
          {forecast && (
            <div className="mt-5 pt-4 border-t border-[#E2E8F0]" data-testid="forecast-breakdown">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold mb-3">Weighted Value per Tahap</div>
              <div className="space-y-2.5">
                {stageOrder.filter((s) => !["won", "lost"].includes(s)).map((s) => {
                  const val = summary.stage_weighted?.[s] || 0;
                  const raw = summary.stage_values?.[s] || 0;
                  const pct = forecast.weighted > 0 ? (val / forecast.weighted) * 100 : 0;
                  return (
                    <div key={s} data-testid={`fc-stage-${s}`}>
                      <div className="flex justify-between items-center text-xs">
                        <span className="uppercase tracking-[0.1em] font-bold text-[11px]" style={{ color: stageColors[s] }}>{t(`stage.${s}`)}</span>
                        <span className="font-mono-data font-semibold text-[#173042] text-xs">{formatIDR(val)} <span className="text-[#64748B] font-normal text-[11px]">/ {formatIDR(raw)}</span></span>
                      </div>
                      <div className="h-1.5 mt-1 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: stageColors[s] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 shadow-2xs">
          <h3 className="font-display text-lg sm:text-2xl tracking-tight text-[#173042] mb-1">Aktivitas Terkini</h3>
          <div className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-[0.12em] font-semibold mb-4">Feed real-time</div>
          {activities.length === 0 && <div className="text-xs text-[#64748B] py-6 text-center">Belum ada aktivitas.</div>}
          <ul className="space-y-3 sm:space-y-3.5" data-testid="recent-activities">
            {activities.map((a) => (
              <li key={a.id} className="border-l-2 border-[#D9A321] pl-3 py-0.5">
                <div className="text-xs sm:text-sm text-[#173042] leading-snug">{a.body}</div>
                <div className="text-[10px] font-mono-data text-[#64748B] mt-1">
                  {new Date(a.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Secondary KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-4 mt-4 sm:mt-5">
        <KPICard testid="kpi-contacts" label={t("kpi.contacts")} icon={UsersThree} accent="#173B4D" value={summary?.contacts ?? "-"} />
        <KPICard testid="kpi-tasks" label={t("kpi.tasks")} icon={ListChecks} accent="#D9A321" value={summary?.open_tasks ?? "-"} />
        <div className="col-span-2 sm:col-span-1">
          <KPICard testid="kpi-won-count" label="Deal Won" icon={Trophy} accent="#2E8B57" value={summary?.won_deals ?? "-"} />
        </div>
      </div>
    </AppLayout>
  );
}

function ForecastTile({ label, value, sub, icon: Icon, highlight, muted, testid }) {
  return (
    <div className="relative z-10" data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-bold ${highlight ? "text-[#D9A321]" : muted ? "text-white/60" : "text-white/80"}`}>
        <Icon size={14} weight={highlight ? "fill" : "regular"} className="shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className={`font-mono-data mt-1 sm:mt-1.5 leading-tight font-bold whitespace-nowrap ${highlight ? "text-base sm:text-xl lg:text-2xl text-[#D9A321]" : "text-sm sm:text-lg text-white"}`}>{value}</div>
      {sub && <div className="text-[9px] sm:text-[10px] text-white/60 mt-0.5 font-mono-data truncate">{sub}</div>}
    </div>
  );
}
